<template>
  <component :is="allSitesMode ? 'BaseLayout' : 'AdminLayout'">
    <template #content>
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h1 class="h4 mb-1">Base de donnée produits ({{ totalRows }})</h1>
          <p class="text-muted mb-0 small">
            Référentiel global des produits. Les opérations du site utilisent uniquement les produits activés.
          </p>
        </div>

        <div class="d-flex gap-2">
          <RouterLink
            v-if="allSitesMode"
            :to="{ name: 'Sites' }"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            title="Retour à la liste des sites"
          >
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour à la liste des sites</span>
          </RouterLink>

          <ModuleActivityButton :module="['products', 'Product', 'CatalogProduct']" label="Base produits" />
          <button
            v-if="canCreateCatalogProduct"
            type="button"
            class="btn btn-primary btn-sm btn-icon-label"
            @click="openCreateProductDialog"
          >
            <i class="fa fa-plus"></i>
            <span class="btn-label ms-1">Nouveau produit</span>
          </button>
          <button
            v-if="canImportCatalogProducts"
            type="button"
            class="btn btn-outline-success btn-sm btn-icon-label"
            @click="openImportDialog"
          >
            <i class="fa fa-file-excel"></i>
            <span class="btn-label ms-1">Importer</span>
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :class="{ active: filtersVisible }"
            @click="filtersVisible = !filtersVisible"
          >
            <i class="fa" :class="filtersVisible ? 'fa-eye-slash' : 'fa-filter'"></i>
            <span class="btn-label ms-1">Filtres</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2 mb-3">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success py-2 mb-3">
        {{ successMessage }}
      </div>

      <div v-show="filtersVisible" class="card module-filter-card mb-3">
        <div class="card-body py-2">
          <div class="row g-2 align-items-end">
            <div v-if="!allSitesMode" class="col-md-4">
              <label class="form-label small mb-1">Statut global</label>
              <PrimevueDropdown
                v-model="filters.global_status"
                :options="globalStatusOptions"
                optionLabel="label"
                optionValue="value"
                class="w-100"
              />
            </div>

            <div class="col-md-4">
              <label class="form-label small mb-1">Statut sur ce site</label>
              <PrimevueDropdown
                v-model="filters.site_status"
                :options="siteStatusOptions"
                optionLabel="label"
                optionValue="value"
                class="w-100"
              />
            </div>

            <div class="col-12 module-filter-actions mt-2">
              <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="resetFilters">
                <i class="fa fa-rotate-left"></i>
                <span class="btn-label ms-1">Réinitialiser</span>
              </button>
              <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="fetchCatalogProducts">
                <i class="fa fa-search"></i>
                <span class="btn-label ms-1">Filtrer</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <BootstrapDataTable
        :response="productsResponse"
        :columns="displayedTableColumns"
        :loading="isLoading"
        :selectable="!allSitesMode && canUpdateCatalogProducts"
        v-model:selected-row-ids="bulkSelectedIds"
        :is-row-selectable="canSelectCatalogProduct"
        :selection-disabled="bulkActionPending"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        :per-page="table.perPage"
        :active-filter-count="activeFilterCount"
        :state-key="allSitesMode ? 'product-database.all-sites' : 'product-database.list'"
        searchable
        v-model:search-value="filters.search"
        search-placeholder="Rechercher nom, code-barres..."
        empty-text="Aucun produit dans la base."
        export-file-name="base-produits-suite241.xlsx"
        print-title="Base de donnée produits"
        @search-change="handleTableSearch"
        @sort-change="handleSortChange"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
        @state-restored="Object.assign(table, { page: $event.page, perPage: $event.perPage })"
        @refresh="fetchCatalogProducts"
        @row-click="openProductDialog"
      >
        <template #bulk-actions="{ selectedCount, isOverLimit }">
          <button type="button" class="btn btn-sm btn-outline-success" :disabled="bulkActionPending || isOverLimit" @click="bulkSetCatalogProductStatus('activate')">
            <i class="fa fa-check me-1"></i>Activer {{ selectedCount }}
          </button>
          <button type="button" class="btn btn-sm btn-outline-danger" :disabled="bulkActionPending || isOverLimit" @click="bulkSetCatalogProductStatus('deactivate')">
            <i class="fa fa-ban me-1"></i>Désactiver {{ selectedCount }}
          </button>
        </template>

        <template #cell-photo="{ row }">
          <div class="text-center">
            <img
              :src="productImage(row)"
              alt="photo produit"
              class="product-database-photo"
            />
          </div>
        </template>

        <template #cell-default_sale_price="{ row }">
          {{ $format.currency(row.default_sale_price || 0) }}
        </template>

        <template #cell-site_status="{ row }">
          <span class="badge" :class="siteStatusClass(row.site_status)">
            {{ siteStatusLabel(row.site_status) }}
          </span>
        </template>
      </BootstrapDataTable>

      <PrimevueDialog
        v-model:visible="productDialogVisible"
        modal
        :header="selectedProduct?.name || 'Produit'"
        :style="{ width: 'min(560px, 96vw)' }"
        :dismissableMask="!isProductActionLoading"
        :closable="!isProductActionLoading"
      >
        <div v-if="selectedProduct" class="product-detail-dialog">
          <div v-if="isProductActionLoading" class="product-detail-overlay" role="status">
            <i class="fa fa-spinner fa-spin me-2"></i>
            Traitement en cours...
          </div>

          <div class="d-flex gap-3 align-items-start mb-3">
            <img
              :src="productImage(selectedProduct)"
              alt="photo produit"
              class="product-database-dialog-photo"
            />
            <div class="min-w-0">
              <h2 class="h6 mb-1">{{ selectedProduct.name }}</h2>
              <div class="small text-muted text-break">
                {{ selectedProduct.description || 'Aucune description renseignée.' }}
              </div>
              <div class="d-flex flex-wrap gap-2 mt-2">
                <span class="badge" :class="selectedProduct.is_active ? 'bg-success' : 'bg-secondary'">
                  {{ selectedProduct.is_active ? 'Global actif' : 'Global inactif' }}
                </span>
                <span v-if="!allSitesMode" class="badge" :class="siteStatusClass(selectedProduct.site_status)">
                  {{ siteStatusLabel(selectedProduct.site_status) }}
                </span>
              </div>
            </div>
          </div>

          <dl class="product-detail-summary mb-3">
            <div>
              <dt>Code-barres</dt>
              <dd>{{ selectedProduct.barcode || '-' }}</dd>
            </div>
            <div>
              <dt>Prix défaut</dt>
              <dd>{{ $format.currency(selectedProduct.default_sale_price || 0) }}</dd>
            </div>
            <div v-if="!allSitesMode">
              <dt>Stock site</dt>
              <dd>{{ selectedProduct.site_product ? selectedProduct.site_product.stock_quantity : '-' }}</dd>
            </div>
            <div>
              <dt>Sites liés</dt>
              <dd>{{ selectedProduct.products_count || 0 }}</dd>
            </div>
          </dl>

          <div class="d-flex flex-wrap justify-content-end gap-2">
            <div v-if="canShowActivationMenu(selectedProduct)" class="btn-group">
              <button
                type="button"
                class="btn btn-success btn-sm dropdown-toggle"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                :disabled="isProductActionLoading"
              >
                <i class="fa fa-check me-1"></i>
                Activer
              </button>
              <ul class="dropdown-menu dropdown-menu-end">
                <li>
                  <button
                    type="button"
                    class="dropdown-item"
                    :disabled="isProductActionLoading || !currentShopId"
                    @click="activateProduct(selectedProduct, 'current_shop')"
                  >
                    <i class="fa fa-check me-1" aria-hidden="true"></i>
                    Activer sur le site connecté ({{ currentShopName }})
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    class="dropdown-item"
                    :disabled="isProductActionLoading"
                    @click="activateProduct(selectedProduct, 'all_active_shops')"
                  >
                    <i class="fa fa-check-double me-1" aria-hidden="true"></i>
                    Dans tous les sites
                  </button>
                </li>
              </ul>
            </div>

            <button
              v-if="!allSitesMode && canUpdateCatalogProducts && selectedProduct.site_status === 'active'"
              type="button"
              class="btn btn-outline-secondary btn-sm btn-icon-label"
              :disabled="isProductActionLoading"
              @click="deactivateProduct(selectedProduct)"
            >
              <i class="fa fa-ban"></i>
              <span class="btn-label ms-1">Désactiver</span>
            </button>
            <button
              v-if="!allSitesMode && selectedProduct.site_status === 'active' && selectedProduct.site_product"
              type="button"
              class="btn btn-primary btn-sm btn-icon-label"
              :disabled="isProductActionLoading"
              @click="openSiteProduct(selectedProduct)"
            >
              <i class="fa fa-eye"></i>
              <span class="btn-label ms-1">Voir</span>
            </button>
            <button
              v-if="!allSitesMode && canDeleteCatalogProduct(selectedProduct)"
              type="button"
              class="btn btn-outline-danger btn-sm btn-icon-label"
              :disabled="isProductActionLoading"
              @click="deleteCatalogProduct(selectedProduct)"
            >
              <i class="fa fa-trash"></i>
              <span class="btn-label ms-1">Supprimer</span>
            </button>
          </div>
        </div>
      </PrimevueDialog>

      <PrimevueDialog
        v-model:visible="createProductDialogVisible"
        modal
        header="Nouveau produit dans la base globale"
        :style="{ width: 'min(720px, 96vw)' }"
        :dismissableMask="!isCreatingProduct"
        :closable="!isCreatingProduct"
      >
        <form
          v-if="createProductDialogVisible"
          class="catalog-product-create-dialog"
          @submit.prevent="submitCreateProduct"
        >
          <div v-if="isCreatingProduct" class="product-detail-overlay" role="status">
            <i class="fa fa-spinner fa-spin me-2"></i>
            Création du produit...
          </div>

          <div class="alert alert-info py-2 mb-3">
            Ce formulaire crée uniquement un produit dans la base globale. Il ne renseigne ni rayon, ni catégorie,
            ni stock, ni paramètres propres à un site. Pour vendre ce produit, activez-le ensuite sur le site voulu.
          </div>

          <div v-if="createProductErrorMessage" class="alert alert-danger py-2 mb-3">
            {{ createProductErrorMessage }}
          </div>

          <div class="row g-3">
            <div class="col-md-8">
              <label class="form-label">Nom du produit <span class="text-danger">*</span></label>
              <input
                v-model.trim="createProductForm.name"
                type="text"
                class="form-control"
                placeholder="Ex: Eau minérale 1,5L"
                required
                :disabled="isCreatingProduct"
              />
            </div>

            <div class="col-md-4">
              <label class="form-label">Statut global</label>
              <div class="form-check form-switch mt-2">
                <input
                  id="catalog-product-is-active"
                  v-model="createProductForm.is_active"
                  class="form-check-input"
                  type="checkbox"
                  :disabled="isCreatingProduct"
                />
                <label class="form-check-label" for="catalog-product-is-active">
                  Actif
                </label>
              </div>
            </div>

            <div class="col-12">
              <div class="form-check form-switch catalog-product-create-option">
                <input
                  id="catalog-product-activate-shops"
                  v-model="createProductForm.activate_in_active_shops"
                  class="form-check-input"
                  type="checkbox"
                  :disabled="isCreatingProduct || !createProductForm.is_active"
                />
                <label class="form-check-label" for="catalog-product-activate-shops">
                  Activer aussi dans tous les sites actifs
                </label>
              </div>
              <div class="form-text">
                Si cette option est cochée, le produit sera créé dans la base globale puis activé dans chaque site actif.
                L’option nécessite que le produit global soit actif.
              </div>
            </div>

            <div class="col-md-6">
              <label class="form-label">SKU / code produit</label>
              <input
                v-model.trim="createProductForm.sku"
                type="text"
                class="form-control"
                placeholder="Généré si vide"
                :disabled="isCreatingProduct"
              />
            </div>

            <div class="col-md-6">
              <label class="form-label">Code-barres</label>
              <input
                v-model.trim="createProductForm.barcode"
                type="text"
                class="form-control"
                placeholder="Généré si vide"
                :disabled="isCreatingProduct"
              />
            </div>

            <div class="col-md-4">
              <label class="form-label">Prix d’achat par défaut</label>
              <input
                v-model="createProductForm.default_purchase_price"
                type="number"
                min="0"
                step="0.01"
                class="form-control"
                placeholder="0"
                :disabled="isCreatingProduct"
              />
            </div>

            <div class="col-md-4">
              <label class="form-label">Prix de vente par défaut</label>
              <input
                v-model="createProductForm.default_sale_price"
                type="number"
                min="0"
                step="0.01"
                class="form-control"
                placeholder="0"
                :disabled="isCreatingProduct"
              />
            </div>

            <div class="col-md-4">
              <label class="form-label">Coefficient par défaut</label>
              <input
                v-model="createProductForm.default_sale_price_coefficient"
                type="number"
                min="0"
                step="0.01"
                class="form-control"
                placeholder="Optionnel"
                :disabled="isCreatingProduct"
              />
            </div>

            <div class="col-12">
              <label class="form-label" for="catalog-product-photo">Photo du produit</label>
              <input
                id="catalog-product-photo"
                ref="createProductPhotoInput"
                type="file"
                class="form-control"
                accept="image/jpeg,image/png,image/webp"
                :disabled="isCreatingProduct"
                @change="onCreateProductPhotoChange"
              />
              <div class="form-text">
                Facultatif. Formats JPG, PNG ou WebP, 10 Mo maximum.
              </div>
              <div v-if="createProductPhotoPreviewUrl" class="catalog-product-photo-preview mt-2">
                <img :src="createProductPhotoPreviewUrl" alt="Aperçu de la photo du produit" />
                <button
                  type="button"
                  class="btn btn-outline-danger btn-sm"
                  :disabled="isCreatingProduct"
                  @click="removeCreateProductPhoto"
                >
                  <i class="fa fa-trash me-1"></i>
                  Retirer
                </button>
              </div>
            </div>

            <div class="col-12">
              <label class="form-label">Description</label>
              <textarea
                v-model.trim="createProductForm.description"
                class="form-control"
                rows="3"
                placeholder="Informations générales du produit"
                :disabled="isCreatingProduct"
              ></textarea>
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2 mt-4">
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              :disabled="isCreatingProduct"
              @click="closeCreateProductDialog"
            >
              <i class="fa fa-xmark me-1" aria-hidden="true"></i>
              Annuler
            </button>
            <button
              type="submit"
              class="btn btn-primary btn-sm"
              :disabled="isCreatingProduct || !createProductForm.name"
            >
              <span v-if="isCreatingProduct">
                <i class="fa fa-spinner fa-spin me-1"></i>
                Création...
              </span>
              <span v-else>
                <i class="fa fa-save me-1"></i>
                Créer dans la base
              </span>
            </button>
          </div>
        </form>
      </PrimevueDialog>

      <PrimevueDialog
        v-model:visible="importDialogVisible"
        modal
        header="Importer dans la base produits"
        :style="{ width: 'min(1120px, 96vw)' }"
        :dismissableMask="!isImporting"
      >
        <div class="import-steps mb-3">
          <span class="step-pill" :class="{ active: importStep === 'guide' }">Préparation</span>
          <span class="step-pill" :class="{ active: importStep === 'results' }">Résultats</span>
        </div>

        <div v-if="importStep === 'guide'">
          <div class="row g-3">
            <div class="col-lg-5">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body">
                  <h6 class="mb-2">Colonnes acceptées</h6>
                  <div class="small text-muted mb-2">
                    Les en-têtes peuvent être en français ou en anglais.
                  </div>

                  <ul class="small mb-3">
                    <li><strong>Obligatoire :</strong> nom</li>
                    <li><strong>Optionnelles :</strong> sku/code, code_barre, prix_vente, prix_achat, coefficient, description, photo_url/photo, actif</li>
                  </ul>

                  <div class="small text-muted mb-3">
                    Si le SKU est vide, il sera généré automatiquement pendant l’import.
                  </div>

                  <div class="d-flex flex-wrap gap-2">
                    <button type="button" class="btn btn-outline-success btn-sm btn-icon-label" @click="downloadTemplate">
                      <i class="fa fa-download"></i>
                      <span class="btn-label ms-1">Modèle Excel</span>
                    </button>
                    <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="triggerFileSelect">
                      <i class="fa fa-folder-open"></i>
                      <span class="btn-label ms-1">Choisir un fichier</span>
                    </button>
                  </div>

                  <input
                    ref="fileInput"
                    type="file"
                    class="d-none"
                    accept=".xlsx,.xls,.csv"
                    @change="onFileChange"
                  />

                  <div v-if="selectedFileName" class="mt-3 small">
                    <span class="text-muted">Fichier :</span>
                    <strong>{{ selectedFileName }}</strong>
                  </div>

                  <div v-if="isParsing" class="mt-3 small text-muted">
                    <i class="fa fa-spinner fa-spin me-1"></i>
                    Lecture du fichier...
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-7">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0">Aperçu des lignes</h6>
                    <span class="badge bg-light text-dark">{{ parsedRows.length }} ligne(s)</span>
                  </div>

                  <div v-if="parseErrorMessage" class="alert alert-danger py-2">
                    {{ parseErrorMessage }}
                  </div>

                  <div v-if="parsedRows.length" class="table-responsive">
                    <table class="table table-sm table-striped table-bordered align-middle mb-0">
                      <thead class="table-light">
                        <tr>
                          <th>Ligne</th>
                          <th>Nom</th>
                          <th>SKU</th>
                          <th>Code-barres</th>
                          <th class="text-end">Prix vente</th>
                          <th class="text-end">Prix achat</th>
                          <th class="text-end">Coef.</th>
                          <th>Actif</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in previewRows" :key="row.__rowNum__">
                          <td>{{ row.__rowNum__ }}</td>
                          <td>{{ row.name || '-' }}</td>
                          <td>{{ row.sku || 'Sera généré' }}</td>
                          <td>{{ row.barcode || '-' }}</td>
                          <td class="text-end">{{ row.sale_price || '-' }}</td>
                          <td class="text-end">{{ row.purchase_price || '-' }}</td>
                          <td class="text-end">{{ row.sale_price_coefficient || '-' }}</td>
                          <td>{{ row.is_active || '-' }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div v-if="parsedRows.length > previewRows.length" class="small text-muted mt-2">
                      Affichage des {{ previewRows.length }} premières lignes seulement.
                    </div>
                  </div>

                  <div v-else class="text-center text-muted py-4">
                    Sélectionne un fichier Excel pour afficher un aperçu.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else>
          <div class="card border-0 shadow-sm mb-3">
            <div class="card-body">
              <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <div>
                  <h6 class="mb-1">Traitement de l’import</h6>
                  <small class="text-muted">
                    Import #{{ importResult?.id || '-' }}
                  </small>
                </div>
                <span class="badge" :class="importStatusClass(importResult?.status)">
                  {{ importStatusLabel(importResult?.status) }}
                </span>
              </div>

              <div class="progress mb-2" style="height: 8px;">
                <div
                  class="progress-bar"
                  role="progressbar"
                  :style="{ width: `${importProgressPercent}%` }"
                  :aria-valuenow="importProgressPercent"
                  aria-valuemin="0"
                  aria-valuemax="100"
                ></div>
              </div>

              <div class="small text-muted">
                {{ importResult?.processed_rows || 0 }} / {{ importResult?.total_rows || parsedRows.length }} ligne(s) traitée(s)
              </div>

              <div v-if="importResult?.error_message" class="alert alert-danger py-2 mt-3 mb-0">
                {{ importResult.error_message }}
              </div>
            </div>
          </div>

          <div class="row g-3 mb-3">
            <div class="col-md-4">
              <div class="card border-0 shadow-sm">
                <div class="card-body py-3">
                  <div class="small text-muted">Importés</div>
                  <div class="h4 mb-0 text-success">{{ importResult?.imported_count || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card border-0 shadow-sm">
                <div class="card-body py-3">
                  <div class="small text-muted">Ignorés</div>
                  <div class="h4 mb-0 text-warning">{{ importResult?.skipped_count || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card border-0 shadow-sm">
                <div class="card-body py-3">
                  <div class="small text-muted">En erreur</div>
                  <div class="h4 mb-0 text-danger">{{ importResult?.failed_count || 0 }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="table-responsive import-results-table">
            <table class="table table-sm table-striped table-hover table-bordered align-middle mb-0">
              <thead class="table-light">
                <tr>
                  <th>Ligne</th>
                  <th>Produit</th>
                  <th>SKU</th>
                  <th>Statut</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in importResultRows" :key="`${row.row_number}-${row.sku}`">
                  <td>{{ row.row_number }}</td>
                  <td>{{ row.name || '-' }}</td>
                  <td>{{ row.sku || '-' }}</td>
                  <td>
                    <span class="badge" :class="importRowStatusClass(row.status)">
                      {{ importRowStatusLabel(row.status) }}
                    </span>
                  </td>
                  <td>{{ row.message || '-' }}</td>
                </tr>
                <tr v-if="!importResultRows.length">
                  <td colspan="5" class="text-center text-muted py-4">
                    Les résultats détaillés apparaîtront pendant le traitement.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <template #footer>
          <div class="d-flex justify-content-between align-items-center gap-2 w-100">
            <button
              v-if="importStep === 'results'"
              class="btn btn-outline-secondary btn-sm btn-icon-label"
              type="button"
              :disabled="isImporting"
              @click="importStep = 'guide'"
            >
              <i class="fa fa-arrow-left"></i>
              <span class="btn-label ms-1">Préparation</span>
            </button>
            <span v-else></span>

            <div class="d-flex justify-content-end gap-2">
              <button class="btn btn-outline-secondary btn-sm" type="button" @click="closeImportDialog">
                <i class="fa fa-xmark me-1" aria-hidden="true"></i>
                Fermer
              </button>
              <button
                v-if="importStep === 'guide'"
                class="btn btn-primary btn-sm"
                type="button"
                @click="submitImport"
                :disabled="!parsedRows.length || isImporting"
              >
                <span v-if="isImporting">
                  <i class="fa fa-spinner fa-spin me-1"></i>
                  Import en cours...
                </span>
                <span v-else>
                  <i class="fa fa-upload me-1"></i>
                  Lancer l’import
                </span>
              </button>
              <button
                v-else
                class="btn btn-outline-primary btn-sm btn-icon-label"
                type="button"
                :disabled="!importResult?.id || isImporting"
                @click="fetchImportStatus"
              >
                <i class="fa fa-rotate-right"></i>
                <span class="btn-label ms-1">Actualiser</span>
              </button>
            </div>
          </div>
        </template>
      </PrimevueDialog>
    </template>
  </component>
</template>

<script>
import { countActiveFilters, extractApiList, extractApiTotal } from '@/support/moduleFilters';
import { productImageUrl } from '@/support/productImages';
import { hasPermission } from '@/support/permissions';
import * as XLSX from 'xlsx';
import { appendPoweredByWorksheet } from '@/support/exportBranding';
import { bulkActionViewMixin } from '@/support/bulkActions';

const DEFAULT_FILTERS = {
  search: '',
  global_status: 'all',
  site_status: 'all',
};
const DEFAULT_CREATE_PRODUCT_FORM = {
  name: '',
  sku: '',
  barcode: '',
  description: '',
  default_purchase_price: '',
  default_sale_price: '',
  default_sale_price_coefficient: '',
  is_active: true,
  activate_in_active_shops: false,
};
const IMPORT_POLLING_DELAY_MS = 2000;
const IMPORT_POLLING_RETRY_DELAY_MS = 5000;
const IMPORT_POLLING_MAX_FAILURES = 3;

export default {
  name: 'ProductDatabaseView',
  mixins: [bulkActionViewMixin],
  props: {
    allSitesMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      productsResponse: null,
      products: [],
      isLoading: false,
      actionLoadingId: null,
      errorMessage: '',
      successMessage: '',
      createProductDialogVisible: false,
      createProductForm: { ...DEFAULT_CREATE_PRODUCT_FORM },
      createProductPhoto: null,
      createProductPhotoPreviewUrl: '',
      createProductErrorMessage: '',
      isCreatingProduct: false,
      productDialogVisible: false,
      selectedProduct: null,
      importDialogVisible: false,
      importStep: 'guide',
      selectedFileName: '',
      parsedRows: [],
      importResult: null,
      parseErrorMessage: '',
      isParsing: false,
      isImporting: false,
      importPollingTimer: null,
      isImportStatusLoading: false,
      importCompletionHandled: false,
      importPollingFailureCount: 0,
      filtersVisible: false,
      filters: { ...DEFAULT_FILTERS },
      table: {
        page: 1,
        perPage: 10,
        sortBy: 'name',
        sortDir: 'asc',
      },
      tableColumns: [
        { key: 'photo', label: 'Photo', style: 'width: 80px;' },
        { key: 'name', label: 'Nom', sortable: true },
        { key: 'barcode', label: 'Code-barres', sortable: true },
        { key: 'default_sale_price', label: 'Prix défaut', sortable: true, class: 'text-end' },
        { key: 'site_status', label: 'Site', class: 'text-center' },
      ],
      globalStatusOptions: [
        { label: 'Tous', value: 'all' },
        { label: 'Actifs', value: 'active' },
        { label: 'Inactifs', value: 'inactive' },
      ],
      siteStatusOptions: [
        { label: 'Tous', value: 'all' },
        { label: 'Activés sur ce site', value: 'active' },
        { label: 'Désactivés sur ce site', value: 'inactive' },
        { label: 'Non activés', value: 'not_activated' },
      ],
    };
  },
  computed: {
    totalRows() {
      return extractApiTotal(this.productsResponse, this.products.length);
    },
    displayedTableColumns() {
      return this.allSitesMode
        ? this.tableColumns.filter((column) => column.key !== 'site_status')
        : this.tableColumns;
    },
    activeFilterCount() {
      return countActiveFilters(this.filters, DEFAULT_FILTERS, ['search']);
    },
    isProductActionLoading() {
      return this.actionLoadingId !== null;
    },
    previewRows() {
      return this.parsedRows.slice(0, 10);
    },
    importResultRows() {
      return this.importResult?.results || [];
    },
    importProgressPercent() {
      const total = Number(this.importResult?.total_rows || this.parsedRows.length || 0);
      const processed = Number(this.importResult?.processed_rows || 0);

      if (!total) return 0;

      return Math.min(100, Math.round((processed / total) * 100));
    },
    currentUser() {
      return this.$store?.getters?.['auth/currentUser']
        || this.$store?.getters?.['auth/user']
        || this.$store?.state?.auth?.user
        || null;
    },
    currentShopId() {
      return this.$store?.getters?.['shop/currentShopId']
        || this.$store?.getters?.['shop/shopInfo']?.id
        || null;
    },
    currentShopName() {
      return this.$store?.getters?.['shop/shopName']
        || this.$store?.getters?.['shop/shopInfo']?.name
        || 'Aucun site';
    },
    canCreateCatalogProduct() {
      return hasPermission(this.currentUser, 'products.create');
    },
    canImportCatalogProducts() {
      return hasPermission(this.currentUser, 'products.import');
    },
    canUpdateCatalogProducts() {
      return hasPermission(this.currentUser, 'products.update');
    },
    canDeleteCatalogProducts() {
      return hasPermission(this.currentUser, 'products.delete');
    },
  },
  mounted() {
    this.fetchCatalogProducts();
  },
  beforeUnmount() {
    this.clearImportPolling();
    this.revokeCreateProductPhotoPreview();
  },
  methods: {
    canSelectCatalogProduct(row) {
      return !this.allSitesMode
        && (Boolean(row?.is_active) || row?.site_status === 'active');
    },

    async bulkSetCatalogProductStatus(action) {
      if (this.allSitesMode) return;

      const isActivation = action === 'activate';
      await this.performBulkAction({
        url: `/product-database/bulk/${action}`,
        method: 'patch',
        itemLabel: 'produit',
        confirmation: (count) => ({
          header: isActivation ? 'Activer les produits' : 'Désactiver les produits',
          message: `${isActivation ? 'Activer' : 'Désactiver'} les ${count} produits sélectionnés sur le site courant ?`,
          acceptLabel: `${isActivation ? 'Activer' : 'Désactiver'} ${count}`,
          acceptClass: isActivation ? 'p-button-success' : 'p-button-danger',
        }),
        refresh: () => this.fetchCatalogProducts(),
      });
    },

    productImage(product) {
      return productImageUrl(product);
    },

    async fetchCatalogProducts(options = {}) {
      const silent = Boolean(options?.silent);

      if (!silent) {
        this.isLoading = true;
        this.errorMessage = '';
        this.successMessage = '';
      }

      try {
        const requestConfig = {
          params: {
            ...this.filters,
            paginate: 1,
            page: this.table.page,
            per_page: this.table.perPage,
            sort_by: this.table.sortBy,
            sort_dir: this.table.sortDir,
            all_sites: this.allSitesMode ? 1 : undefined,
          },
        };

        if (silent) {
          requestConfig.skipErrorToast = true;
        }

        const response = await this.$api.get('/product-database', requestConfig);

        this.productsResponse = response.data;
        this.products = extractApiList(response.data);
        this.syncSelectedProductFromList();
      } catch (error) {
        console.error('Erreur chargement base produits', error);
        if (!silent) {
          this.errorMessage = 'Impossible de charger la base de donnée produits.';
        }
      } finally {
        if (!silent) {
          this.isLoading = false;
        }
      }
    },

    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
      this.table.page = 1;
      this.fetchCatalogProducts();
    },

    openProductDialog(row) {
      this.selectedProduct = row;
      this.productDialogVisible = true;
      this.errorMessage = '';
      this.successMessage = '';
    },

    canActivateCatalogProduct(row) {
      return this.canUpdateCatalogProducts && row?.site_status !== 'active' && row?.is_active;
    },

    canActivateInAllSites(row) {
      return this.canUpdateCatalogProducts && row?.is_active;
    },

    canShowActivationMenu(row) {
      return this.allSitesMode
        ? this.canActivateInAllSites(row)
        : this.canActivateCatalogProduct(row);
    },

    async activateProduct(row, scope = 'current_shop') {
      if (!this.canUpdateCatalogProducts) return;
      if (scope === 'current_shop' && !this.currentShopId) return;

      if (scope === 'all_active_shops') {
        const ok = await this.$confirmAction(`Activer "${row.name}" dans tous les sites actifs ?`);
        if (!ok) return;
      }

      this.actionLoadingId = row.id;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const response = await this.$api.post(`/product-database/${row.id}/activate`, { scope });
        const updatedProduct = response.data?.data?.catalog_product;
        const summary = response.data?.data?.activation_summary;

        if (updatedProduct) {
          this.applyCatalogProductUpdate(updatedProduct);
        }

        this.successMessage = scope === 'all_active_shops' && summary
          ? `Produit activé dans ${summary.processed_count || 0} site(s).`
          : (response.data?.message || 'Produit activé pour ce site.');
      } catch (error) {
        console.error('Erreur activation produit', error);
        this.errorMessage = this.apiErrorMessage(error, 'Impossible d’activer ce produit pour le site.');
      } finally {
        this.actionLoadingId = null;
      }
    },

    async deactivateProduct(row) {
      if (!this.canUpdateCatalogProducts) return;

      const ok = await this.$confirmAction(`Désactiver "${row.name}" pour ce site ?`);
      if (!ok) return;

      this.actionLoadingId = row.id;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const response = await this.$api.patch(`/product-database/${row.id}/deactivate`);
        const updatedProduct = response.data?.data?.catalog_product;

        if (updatedProduct) {
          this.applyCatalogProductUpdate(updatedProduct);
        }

        this.successMessage = response.data?.message || 'Produit désactivé pour ce site.';
      } catch (error) {
        console.error('Erreur désactivation produit', error);
        this.errorMessage = this.apiErrorMessage(error, 'Impossible de désactiver ce produit pour le site.');
      } finally {
        this.actionLoadingId = null;
      }
    },

    canDeleteCatalogProduct(row) {
      return this.canDeleteCatalogProducts && Number(row.products_count || 0) === 0;
    },

    async deleteCatalogProduct(row) {
      if (!this.canDeleteCatalogProduct(row)) return;

      const ok = await this.$confirmAction(`Supprimer définitivement "${row.name}" de la base produits ? Ce produit n’est activé sur aucun site.`);
      if (!ok) return;

      this.actionLoadingId = row.id;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        await this.$api.delete(`/product-database/${row.id}`);
        this.removeCatalogProductFromList(row.id);
        this.productDialogVisible = false;
        this.selectedProduct = null;
        this.successMessage = 'Produit supprimé de la base.';
      } catch (error) {
        console.error('Erreur suppression produit catalogue', error);
        this.errorMessage = this.apiErrorMessage(error, 'Impossible de supprimer ce produit de la base.');
      } finally {
        this.actionLoadingId = null;
      }
    },

    openSiteProduct(row) {
      if (!row.site_product?.id) return;
      this.$router.push({ name: 'ProductShow', params: { id: row.site_product.id } });
    },

    applyCatalogProductUpdate(row) {
      if (this.catalogProductMatchesFilters(row)) {
        this.replaceCatalogProductInList(row);
        this.selectedProduct = row;
        return;
      }

      this.removeCatalogProductFromList(row.id);
      this.productDialogVisible = false;
      this.selectedProduct = null;
    },

    replaceCatalogProductInList(row) {
      const index = this.products.findIndex((product) => product.id === row.id);
      const rows = [...this.products];

      if (index >= 0) {
        rows.splice(index, 1, row);
      } else {
        rows.unshift(row);
      }

      this.products = rows;
      this.setProductsResponseRows(rows);
    },

    removeCatalogProductFromList(id) {
      const before = this.products.length;
      const rows = this.products.filter((product) => product.id !== id);
      const removed = before !== rows.length;

      this.products = rows;
      this.setProductsResponseRows(rows, removed ? -1 : 0);
    },

    setProductsResponseRows(rows, totalDelta = 0) {
      if (!this.productsResponse) return;

      const meta = this.productsResponse.meta || {};
      const pagination = meta.pagination;
      const nextMeta = { ...meta };

      if (pagination && totalDelta !== 0) {
        const total = Math.max(0, Number(pagination.total || 0) + totalDelta);
        const from = rows.length ? pagination.from : null;
        const to = rows.length && pagination.from !== null
          ? Number(pagination.from || 0) + rows.length - 1
          : null;

        nextMeta.pagination = {
          ...pagination,
          from,
          total,
          to,
        };
      }

      this.productsResponse = {
        ...this.productsResponse,
        data: rows,
        meta: nextMeta,
      };
    },

    syncSelectedProductFromList() {
      if (!this.selectedProduct) return;

      const freshRow = this.products.find((product) => product.id === this.selectedProduct.id);

      if (freshRow) {
        this.selectedProduct = freshRow;
      }
    },

    catalogProductMatchesFilters(row) {
      const term = this.filters.search.trim().toLowerCase();

      if (term) {
        const haystack = [
          row.name,
          row.sku,
          row.barcode,
          row.description,
        ].filter(Boolean).join(' ').toLowerCase();

        if (!haystack.includes(term)) {
          return false;
        }
      }

      if (this.filters.global_status === 'active' && !row.is_active) {
        return false;
      }

      if (this.filters.global_status === 'inactive' && row.is_active) {
        return false;
      }

      if (this.filters.site_status !== 'all' && row.site_status !== this.filters.site_status) {
        return false;
      }

      return true;
    },

    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
      this.table.page = 1;
      this.fetchCatalogProducts();
    },

    handlePageChange(page) {
      this.table.page = page;
      this.fetchCatalogProducts();
    },

    handlePerPageChange(perPage) {
      this.table.perPage = perPage;
      this.table.page = 1;
      this.fetchCatalogProducts();
    },

    handleTableSearch(search) {
      this.filters.search = search;
      this.table.page = 1;
      this.fetchCatalogProducts();
    },

    openCreateProductDialog() {
      if (!this.canCreateCatalogProduct) return;

      this.resetCreateProductForm();
      this.createProductDialogVisible = true;
      this.errorMessage = '';
      this.successMessage = '';
    },

    closeCreateProductDialog() {
      if (this.isCreatingProduct) return;

      this.createProductDialogVisible = false;
      this.resetCreateProductForm();
    },

    resetCreateProductForm() {
      this.revokeCreateProductPhotoPreview();
      this.createProductForm = { ...DEFAULT_CREATE_PRODUCT_FORM };
      this.createProductPhoto = null;
      this.createProductErrorMessage = '';
      this.isCreatingProduct = false;

      const input = this.$refs.createProductPhotoInput;
      if (input && input.value !== undefined) input.value = '';
    },

    onCreateProductPhotoChange(event) {
      const file = event.target?.files?.[0] || null;
      this.revokeCreateProductPhotoPreview();
      this.createProductPhoto = file;
      this.createProductPhotoPreviewUrl = file ? URL.createObjectURL(file) : '';
    },

    revokeCreateProductPhotoPreview() {
      if (this.createProductPhotoPreviewUrl) URL.revokeObjectURL(this.createProductPhotoPreviewUrl);
      this.createProductPhotoPreviewUrl = '';
    },

    removeCreateProductPhoto() {
      this.revokeCreateProductPhotoPreview();
      this.createProductPhoto = null;
      const input = this.$refs.createProductPhotoInput;
      if (input && input.value !== undefined) input.value = '';
    },

    createProductPayload() {
      const payload = new FormData();
      Object.entries({
        name: this.createProductForm.name,
        sku: this.createProductForm.sku,
        barcode: this.createProductForm.barcode,
        description: this.createProductForm.description,
        default_purchase_price: this.createProductForm.default_purchase_price,
        default_sale_price: this.createProductForm.default_sale_price,
        default_sale_price_coefficient: this.createProductForm.default_sale_price_coefficient,
        is_active: this.createProductForm.is_active ? '1' : '0',
      }).forEach(([key, value]) => payload.append(key, value ?? ''));
      if (this.createProductPhoto) payload.append('photo', this.createProductPhoto);
      return payload;
    },

    async submitCreateProduct() {
      if (!this.canCreateCatalogProduct) return;

      if (!this.createProductForm.name) {
        this.createProductErrorMessage = 'Le nom du produit est obligatoire.';
        return;
      }

      this.isCreatingProduct = true;
      this.createProductErrorMessage = '';
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const response = await this.$api.post('/product-database', this.createProductPayload());
        const catalogProductId = response.data?.data?.id;

        let activationSummary = null;
        if (this.createProductForm.activate_in_active_shops && this.createProductForm.is_active) {
          if (!catalogProductId) {
            throw new Error('Produit créé, mais activation automatique impossible: identifiant produit introuvable.');
          }

          const activationResponse = await this.$api.post(`/product-database/${catalogProductId}/activate`, {
            scope: 'all_active_shops',
          });
          activationSummary = activationResponse.data?.data?.activation_summary;
        }

        this.successMessage = activationSummary
          ? `Produit créé et activé dans ${activationSummary.processed_count || 0} site(s) actif(s).`
          : (response.data?.message || 'Produit créé dans la base globale.');
        this.createProductDialogVisible = false;
        this.resetCreateProductForm();
        await this.fetchCatalogProducts({ silent: true });
      } catch (error) {
        console.error('Erreur création produit catalogue', error);
        this.createProductErrorMessage = this.apiErrorMessage(error, 'Impossible de créer ce produit dans la base globale.');
      } finally {
        this.isCreatingProduct = false;
      }
    },

    sleep(delay) {
      return new Promise((resolve) => {
        window.setTimeout(resolve, delay);
      });
    },

    openImportDialog() {
      if (!this.canImportCatalogProducts) return;

      this.resetImportState();
      this.importDialogVisible = true;
    },

    closeImportDialog() {
      if (this.isImporting) return;

      this.importDialogVisible = false;
      this.resetImportState();
    },

    resetImportState() {
      this.importStep = 'guide';
      this.selectedFileName = '';
      this.parsedRows = [];
      this.importResult = null;
      this.parseErrorMessage = '';
      this.isParsing = false;
      this.isImporting = false;
      this.isImportStatusLoading = false;
      this.importCompletionHandled = false;
      this.importPollingFailureCount = 0;
      this.clearImportPolling();

      const input = this.$refs.fileInput;
      if (input && input.value !== undefined) {
        input.value = '';
      }
    },

    triggerFileSelect() {
      const input = this.$refs.fileInput;
      if (input && typeof input.click === 'function') input.click();
    },

    onFileChange(event) {
      const input = event.target;
      const file = input.files && input.files[0];
      if (!file) return;

      this.selectedFileName = file.name;
      this.parsedRows = [];
      this.importResult = null;
      this.parseErrorMessage = '';
      this.readExcelFile(file);
    },

    readExcelFile(file) {
      this.isParsing = true;
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '', raw: false });

          this.parsedRows = this.normalizeImportRows(jsonData);

          if (!this.parsedRows.length) {
            this.parseErrorMessage = 'Le fichier ne contient aucune ligne à importer.';
          }
        } catch (error) {
          console.error('Erreur lecture Excel base produits', error);
          this.parseErrorMessage = 'Impossible de lire le fichier Excel. Vérifiez le format.';
        } finally {
          this.isParsing = false;
        }
      };

      reader.onerror = () => {
        this.isParsing = false;
        this.parseErrorMessage = 'Erreur lors de la lecture du fichier.';
      };

      reader.readAsArrayBuffer(file);
    },

    async submitImport() {
      if (!this.canImportCatalogProducts) return;

      if (!this.parsedRows.length) {
        alert("Veuillez sélectionner un fichier et vérifier qu'il contient des lignes.");
        return;
      }

      this.isImporting = true;
      this.importResult = null;
      this.importStep = 'results';
      this.isImportStatusLoading = false;
      this.importCompletionHandled = false;
      this.importPollingFailureCount = 0;

      try {
        const response = await this.$api.post('/product-database/import', {
          rows: this.parsedRows,
        });

        this.importResult = response.data.data;
        this.startImportPolling();
      } catch (error) {
        console.error('Erreur import base produits', error);
        let message = "Une erreur est survenue pendant l’import.";
        if (error?.response?.data?.errors) {
          const raw = Object.values(error.response.data.errors);
          const flat = raw.reduce((acc, value) => acc.concat(value), []);
          message = flat.join(' ');
        }

        this.importResult = {
          status: 'failed',
          error_message: message,
          total_rows: this.parsedRows.length,
          processed_rows: 0,
          imported_count: 0,
          skipped_count: 0,
          failed_count: 0,
          results: [],
        };
        this.isImporting = false;
      }
    },

    async fetchImportStatus(options = {}) {
      const automatic = Boolean(options?.automatic);
      const importId = this.importResult?.id;

      if (!importId || this.isImportStatusLoading || this.importCompletionHandled) return;

      this.isImportStatusLoading = true;
      try {
        const response = await this.$api.get(`/product-database/imports/${importId}`, { skipErrorToast: true });

        if (!this.importResult || this.importResult.id !== importId) return;

        this.importPollingFailureCount = 0;
        this.importResult = response.data.data;

        if (['completed', 'failed'].includes(this.importResult.status)) {
          this.isImporting = false;
          this.clearImportPolling();

          if (this.importCompletionHandled) return;

          this.importCompletionHandled = true;

          if (this.importResult.status === 'completed') {
            await this.fetchCatalogProducts({ silent: true });
          }

          return;
        }

        if (automatic || this.isImporting) {
          this.scheduleImportPolling();
        }
      } catch (error) {
        console.error('Erreur suivi import base produits', error);

        if (!this.importResult || this.importResult.id !== importId) return;

        this.importPollingFailureCount += 1;

        if (this.importPollingFailureCount >= IMPORT_POLLING_MAX_FAILURES) {
          this.isImporting = false;
          this.clearImportPolling();
          this.errorMessage = 'Le suivi automatique de l’import est temporairement indisponible. Vous pouvez actualiser manuellement.';

          return;
        }

        if (automatic || this.isImporting) {
          this.scheduleImportPolling(IMPORT_POLLING_RETRY_DELAY_MS);
        }
      } finally {
        if (this.importResult?.id === importId) {
          this.isImportStatusLoading = false;
        }
      }
    },

    startImportPolling() {
      this.clearImportPolling();
      this.isImporting = true;
      this.isImportStatusLoading = false;
      this.importCompletionHandled = false;
      this.importPollingFailureCount = 0;
      this.fetchImportStatus({ automatic: true });
    },

    scheduleImportPolling(delay = IMPORT_POLLING_DELAY_MS) {
      this.clearImportPolling();

      if (!this.isImporting || !this.importResult?.id || this.importCompletionHandled) return;

      this.importPollingTimer = window.setTimeout(() => {
        this.importPollingTimer = null;
        this.fetchImportStatus({ automatic: true });
      }, delay);
    },

    clearImportPolling() {
      if (this.importPollingTimer) {
        window.clearTimeout(this.importPollingTimer);
        this.importPollingTimer = null;
      }
    },

    normalizeImportRows(rows) {
      return (rows || [])
        .map((row, index) => {
          const normalized = {};

          Object.entries(row || {}).forEach(([key, value]) => {
            normalized[this.normalizeImportKey(key)] = value;
          });

          return {
            __rowNum__: row.__rowNum__ || index + 2,
            name: this.pickImportValue(normalized, ['name', 'nom', 'nom_produit', 'produit', 'designation', 'libelle']),
            sku: this.pickImportValue(normalized, ['sku', 'code', 'reference', 'ref', 'code_produit']),
            barcode: this.pickImportValue(normalized, ['barcode', 'code_barre', 'codebarre', 'ean', 'ean13', 'gtin']),
            photo: this.pickImportValue(normalized, ['photo', 'image', 'url_photo', 'photo_url']),
            description: this.pickImportValue(normalized, ['description', 'desc']),
            purchase_price: this.pickImportValue(normalized, ['purchase_price', 'prix_achat', 'prix_d_achat', 'pa', 'achat']),
            sale_price: this.pickImportValue(normalized, ['sale_price', 'prix_vente', 'prix_de_vente', 'pv', 'vente']),
            sale_price_coefficient: this.pickImportValue(normalized, ['sale_price_coefficient', 'default_sale_price_coefficient', 'coefficient', 'coef', 'coefficient_prix_vente']),
            is_active: this.pickImportValue(normalized, ['is_active', 'actif', 'active', 'statut']),
          };
        })
        .filter((row) => Object.entries(row).some(([key, value]) => {
          return key !== '__rowNum__' && value !== '' && value !== null && value !== undefined;
        }));
    },

    normalizeImportKey(key) {
      return String(key || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
    },

    pickImportValue(row, keys) {
      const key = keys.find((candidate) => row[candidate] !== undefined && row[candidate] !== null && row[candidate] !== '');
      return key ? row[key] : '';
    },

    importStatusLabel(status) {
      if (status === 'queued') return 'En attente';
      if (status === 'processing') return 'En cours';
      if (status === 'completed') return 'Terminé';
      if (status === 'failed') return 'Échec';
      return status || 'Non lancé';
    },

    importStatusClass(status) {
      if (status === 'completed') return 'bg-success';
      if (status === 'failed') return 'bg-danger';
      if (status === 'processing') return 'bg-primary';
      return 'bg-secondary';
    },

    importRowStatusLabel(status) {
      if (status === 'imported') return 'Importé';
      if (status === 'skipped') return 'Ignoré';
      if (status === 'failed') return 'Erreur';
      return status || '-';
    },

    importRowStatusClass(status) {
      if (status === 'imported') return 'bg-success';
      if (status === 'skipped') return 'bg-warning text-dark';
      if (status === 'failed') return 'bg-danger';
      return 'bg-secondary';
    },

    downloadTemplate() {
      const headers = [
        'nom',
        'sku',
        'code_barre',
        'prix_vente',
        'prix_achat',
        'coefficient',
        'description',
        'photo_url',
        'actif',
      ];
      const exampleRow = [
        'Riz parfumé 5kg',
        '',
        '3760000000012',
        7500,
        5600,
        1.34,
        'Sac de riz parfumé format familial',
        'https://example.com/images/riz-parfume-5kg.jpg',
        'oui',
      ];

      const worksheet = XLSX.utils.aoa_to_sheet([headers, exampleRow]);
      appendPoweredByWorksheet(worksheet);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Base produits');

      XLSX.writeFile(workbook, 'modele-base-produits-suite241-gestion-commerciale.xlsx');
    },

    siteStatusLabel(status) {
      if (status === 'active') return 'Activé';
      if (status === 'inactive') return 'Désactivé';
      return 'Non activé';
    },

    siteStatusClass(status) {
      if (status === 'active') return 'bg-success';
      if (status === 'inactive') return 'bg-secondary';
      return 'bg-light text-dark';
    },

    apiErrorMessage(error, fallback) {
      const errors = error?.response?.data?.errors;
      if (!errors) return fallback;

      return Object.values(errors).flat().join(' ') || fallback;
    },
  },
};
</script>

<style scoped>
.import-steps {
  display: flex;
  gap: 0.5rem;
}

.step-pill {
  border: 1px solid #d6d6d6;
  border-radius: 999px;
  color: #6c757d;
  font-size: 0.85rem;
  padding: 0.35rem 0.75rem;
}

.step-pill.active {
  background: var(--bs-primary);
  border-color: var(--bs-primary);
  color: #fff;
}

.import-results-table {
  max-height: 320px;
}

.product-database-photo {
  border-radius: 4px;
  height: 40px;
  object-fit: cover;
  width: 40px;
}

.product-detail-dialog {
  position: relative;
}

.catalog-product-create-dialog {
  position: relative;
}

.catalog-product-photo-preview {
  align-items: center;
  display: flex;
  gap: 0.75rem;
}

.catalog-product-photo-preview img {
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  height: 6rem;
  object-fit: cover;
  width: 6rem;
}

.catalog-product-create-option {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.75rem 0.75rem 0.75rem 2.75rem;
}

.product-detail-overlay {
  align-items: center;
  background: rgba(255, 255, 255, 0.78);
  border-radius: 0.5rem;
  color: #1f2937;
  display: flex;
  font-weight: 600;
  inset: 0;
  justify-content: center;
  position: absolute;
  z-index: 3;
}

.product-database-dialog-photo {
  border-radius: 0.45rem;
  height: 4.25rem;
  object-fit: cover;
  width: 4.25rem;
}

.product-detail-summary {
  display: grid;
  gap: 0.7rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.product-detail-summary > div {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 0.45rem;
  padding: 0.65rem;
}

.product-detail-summary dt {
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.2rem;
}

.product-detail-summary dd {
  font-size: 0.9rem;
  margin-bottom: 0;
  overflow-wrap: anywhere;
}

@media (max-width: 575.98px) {
  .product-detail-summary {
    grid-template-columns: 1fr;
  }
}
</style>
